import moment from 'moment';

// A selected date range, as used by the ngx-daterangepicker-material input.
export interface SelectionDateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

// Date ranges are stored on favorites as a preset label ("Last 7 Days") when one
// is selected, so the favorite keeps rolling forward, or as "start,end" otherwise.
// This is the encoding the Chart Favorites use (review-readings encodeDateRange /
// applyDateRange), plus the "Last N hrs" quick picks, which those two miss.

const HOUR_PRESETS: { [name: string]: number } = {
  'Last 4 hrs': 4,
  'Last 8 hrs': 8,
  'Last 12 hrs': 12,
  'Last 24 hrs': 24
};


// Fresh moments for a preset (the DateRanges constant is evaluated once at load, so
// its "Last 7 Days" etc. go stale in a long-running session). Null for an unknown name.
export function presetDateRange(name: string): SelectionDateRange | null {
  if (HOUR_PRESETS[name] != undefined) {
    return { startDate: moment().add(-HOUR_PRESETS[name], 'hours'), endDate: moment() };
  }

  switch (name) {
    case 'Today':
      return { startDate: moment().startOf('day'), endDate: moment().endOf('day') };
    case 'Yesterday':
      return { startDate: moment().subtract(1, 'days').startOf('day'), endDate: moment().subtract(1, 'days').endOf('day') };
    case 'Last 3 Days':
      return { startDate: moment().subtract(2, 'days').startOf('day'), endDate: moment().endOf('day') };
    case 'Last 7 Days':
      return { startDate: moment().subtract(6, 'days'), endDate: moment() };
    case 'Last 30 Days':
      return { startDate: moment().subtract(29, 'days'), endDate: moment() };
    case 'This Month':
      return { startDate: moment().startOf('month'), endDate: moment().endOf('month') };
    case 'Last Month':
      return { startDate: moment().subtract(1, 'month').startOf('month'), endDate: moment().subtract(1, 'month').endOf('month') };
    default:
      return null;
  }
}


export function encodeDateRange(name: string, range: SelectionDateRange): string {
  if (name != undefined && name !== '' && name !== 'Custom Range') {
    return name;
  }
  return range.startDate.format('YYYY-MM-DDTHH:mm:ss') + ',' + range.endDate.format('YYYY-MM-DDTHH:mm:ss');
}


export function decodeDateRange(encoded: string): { range: SelectionDateRange; name: string } | null {
  if (encoded == undefined || encoded === '') {
    return null;
  }

  const preset = presetDateRange(encoded);
  if (preset != null) {
    return { range: preset, name: encoded };
  }

  const parts = encoded.split(',');
  if (parts.length !== 2) {
    return null;
  }

  const start = moment(parts[0]);
  const end = moment(parts[1]);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return { range: { startDate: start, endDate: end }, name: 'Custom Range' };
}
